import { useState, useEffect, useCallback, useRef } from 'react'

export interface IPInfo {
  ip: string
  location: string
  colo: string
  country: string
  isLoading: boolean
  error: string | null
  lastUpdated: Date | null
}

interface CloudflareTraceData {
  ip?: string
  loc?: string
  colo?: string
  [key: string]: string | undefined
}

const parseCloudflareTrace = (text: string): CloudflareTraceData => {
  const data: CloudflareTraceData = {}
  text.split('\n').forEach((line) => {
    const [key, value] = line.split('=')
    if (key && value) {
      data[key.trim()] = value.trim()
    }
  })
  return data
}

interface IPEndpoint {
  url: string
  parse: (data: unknown) => { ip: string; country?: string }
  isText?: boolean
}

const IP_ENDPOINTS: IPEndpoint[] = [
  {
    url: 'https://cloudflare.com/cdn-cgi/trace',
    isText: true,
    parse: (text) => {
      const data = parseCloudflareTrace(text as string)
      return { ip: data.ip || '', country: data.loc }
    },
  },
  {
    url: 'https://api.ipify.org?format=json',
    parse: (r: unknown) => ({ ip: (r as { ip: string }).ip }),
  },
  {
    url: 'https://ipapi.co/json/',
    parse: (r: unknown) => {
      const data = r as { ip: string; country_code?: string }
      return { ip: data.ip, country: data.country_code }
    },
  },
  {
    url: 'https://api.my-ip.io/v2/ip.json',
    parse: (r: unknown) => {
      const data = r as { ip: string; country?: { code?: string } }
      return { ip: data.ip, country: data.country?.code }
    },
  },
  {
    url: 'https://httpbin.org/ip',
    parse: (r: unknown) => ({ ip: (r as { origin: string }).origin }),
  },
]

const fetchIPWithFallbacks = async (): Promise<{ ip: string; country?: string }> => {
  for (const endpoint of IP_ENDPOINTS) {
    try {
      const res = await fetch(endpoint.url)
      if (!res.ok) continue

      const data = endpoint.isText ? await res.text() : await res.json()
      const result = endpoint.parse(data)

      if (result.ip) {
        return result
      }
    } catch {
      continue
    }
  }
  throw new Error('Could not determine public IP')
}

const COUNTRY_FLAGS: Record<string, string> = {
  US: '🇺🇸',
  GB: '🇬🇧',
  CA: '🇨🇦',
  AU: '🇦🇺',
  DE: '🇩🇪',
  FR: '🇫🇷',
  JP: '🇯🇵',
  KR: '🇰🇷',
  CN: '🇨🇳',
  RU: '🇷🇺',
  BR: '🇧🇷',
  IN: '🇮🇳',
  MX: '🇲🇽',
  ES: '🇪🇸',
  IT: '🇮🇹',
  NL: '🇳🇱',
  SE: '🇸🇪',
  CH: '🇨🇭',
  PL: '🇵🇱',
  SG: '🇸🇬',
}

export const getCountryFlag = (countryCode: string): string => {
  return COUNTRY_FLAGS[countryCode] || '🌍'
}

export interface UseIPDetectionOptions {
  pollInterval?: number
  onIPChange?: (oldIP: string, newIP: string) => void
}

export const useIPDetection = (options: UseIPDetectionOptions = {}) => {
  const { pollInterval = 30000, onIPChange } = options

  const [ipInfo, setIPInfo] = useState<IPInfo>({
    ip: '',
    location: '',
    colo: '',
    country: '',
    isLoading: true,
    error: null,
    lastUpdated: null,
  })

  const [previousIP, setPreviousIP] = useState<string | null>(null)
  const [ipHistory, setIPHistory] = useState<Array<{ ip: string; timestamp: Date }>>([])
  const onIPChangeRef = useRef(onIPChange)

  useEffect(() => {
    onIPChangeRef.current = onIPChange
  }, [onIPChange])

  const fetchIP = useCallback(async () => {
    try {
      const result = await fetchIPWithFallbacks()

      const newIP = result.ip || 'Unknown'
      const newCountry = result.country || 'Unknown'

      setIPInfo((prev) => {
        if (prev.ip && prev.ip !== newIP && prev.ip !== 'Unknown') {
          setPreviousIP(prev.ip)
          setIPHistory((h) => [...h.slice(-9), { ip: newIP, timestamp: new Date() }])
          onIPChangeRef.current?.(prev.ip, newIP)
        }

        return {
          ip: newIP,
          location: newCountry,
          colo: '',
          country: newCountry,
          isLoading: false,
          error: null,
          lastUpdated: new Date(),
        }
      })
    } catch (err) {
      setIPInfo((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch IP',
      }))
    }
  }, [])

  useEffect(() => {
    fetchIP()

    const interval = setInterval(fetchIP, pollInterval)
    return () => clearInterval(interval)
  }, [fetchIP, pollInterval])

  const refresh = useCallback(() => {
    setIPInfo((prev) => ({ ...prev, isLoading: true }))
    fetchIP()
  }, [fetchIP])

  return {
    ...ipInfo,
    previousIP,
    ipHistory,
    refresh,
    hasIPChanged: previousIP !== null && previousIP !== ipInfo.ip,
  }
}

export default useIPDetection
